import { useFormikContext } from "formik";
import {
  CommonFormLayout,
  Modal,
  Textarea,
  Toggle,
  UploadButton,
  WarningBox,
} from "@/components/common";
import { useAllShipmentServicesRequest } from "@/api/useShipmentServicesRequest";
import {
  additionalServicesIcons,
  deliveryAllowedStatuses,
} from "@/utils/additional";
import { useCourier } from "@/context/Courier.context";
import { useParcels } from "@/context/Parcels.context";
import { UploadIcon } from "@/components/assets/icons";
import { useModal } from "@/hooks/useModal";
import ParcelDividerModalUz from "./uz/parcel-divider-modal-uz";
import ParcelDividerModalUa from "./ua/parcel-divider-modal-ua";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetViewParcelRequest } from "@/api/useGetViewParcelRequest";
import { ParcelStatus, Theme } from "@/enums";
import { toast } from "react-toastify";
import { useGetShipmentDataToCreateRequest } from "@/api/useGetShipmentDataToCreateRequest";
import { useTranslations } from "next-intl";
import { useCancelInsuranceForShipmentRequest } from "@/api/useCancelInsuranceForShipmentRequest";
import { useTheme } from "@/context/Theme.context";
import ParcelGuaranteeGeModal from "./ge/parcel-guarantee-modal-ge";
import { getTotalPrice } from "./ge/parcel-items-ge";
import { useGetCalculateShipmentRequest } from "@/api/useGetCalculateShipomentRequest";
import { nonEditableStatusesForDeliveryTypeAndServices } from "@/utils/customUtils";
import { formatNumber } from "@/utils/formatNumber";

enum ParcelModalType {
  DIVIDE = "DIVIDE",
  GUARANTEE = "GUARANTEE",
}

export const parcelServiceEditableStatuses = [
  ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE,
  ParcelStatus.COMMON_STATUS_RECEIVED_AT_THE_INSTOCK,
];

const ParcelServices = () => {
  const t = useTranslations();
  const { setCourierModalOpen, deliveryRequestStatus } = useCourier();
  const theme = useTheme();

  const [modalType, setModalType] = useState<ParcelModalType | null>(null);

  // if theme is ge true else false noSubmit state
  const validationStepStatusRef = useRef<{
    invoiceFile: boolean;
    additionalInsurance: boolean;
    customDeclaration: boolean;
    customPriceLimit: boolean;
  }>({
    invoiceFile: true,
    additionalInsurance: false,
    customDeclaration: true,
    customPriceLimit: true,
  });

  const { modalOpen, openModal, closeModal } = useModal();
  const { activeStep, setActiveStep, parcelStatus, parcelId } = useParcels();

  const { data: currentParcelData } = useGetViewParcelRequest(
    parcelStatus &&
      !parcelServiceEditableStatuses.includes(parcelStatus) &&
      parcelId,
  );

  const {
    values,
    setFieldValue,
    validateForm,
    errors,
    setFieldTouched,
    validateField,
    submitForm,
    setErrors,
  } = useFormikContext<any>();
  const {
    services,
    servicePhotoOfContentsComment,
    serviceFunctionalityCheckComment,
    products,
  } = values;

  const { data: dataToCreate } = useGetShipmentDataToCreateRequest(parcelId);
  const createParcelData = dataToCreate?.data?.data;
  const { data: editableServices } = useAllShipmentServicesRequest(true);

  const { data } = useGetCalculateShipmentRequest();
  const shipmenPrices = data?.data?.data;

  useEffect(() => {
    if (!parcelId && createParcelData?.shipmentServiceIds) {
      const filteredServices = createParcelData?.shipmentServiceIds?.filter(
        (service) =>
          editableServices?.data?.data.some((e) => e.serviceId === service),
      );
      !services?.length && setFieldValue("services", filteredServices);
    }
  }, [createParcelData, parcelId, editableServices]);

  const insuranceIsCancelable = useMemo(() => {
    if (
      parcelStatus === ParcelStatus.COMMON_STATUS_PROCESSED_AT_THE_INSTOCK ||
      parcelStatus ===
        ParcelStatus.COMMON_STATUS_PREPARED_SHIPPING_FROM_FCOUNTRY ||
      parcelStatus === ParcelStatus.COMMON_STATUS_SHIPPED_FROM_FCOUNTRY
    ) {
      return true;
    }
  }, [parcelStatus]);

  const cancelInsuranceRequest = useCancelInsuranceForShipmentRequest();

  const isEditable = useMemo(() => {
    if (parcelServiceEditableStatuses.includes(parcelStatus) || !parcelId) {
      return true;
    } else return false;
  }, [parcelStatus, parcelId]);

  const handleToggle = (serviceId: number) => {
    if (!services?.length) {
      setFieldValue("services", [serviceId]);
    } else if (!!services?.find((ser) => ser === serviceId)) {
      const filteredServices = services?.filter((ser) => ser !== serviceId);
      if (services !== filteredServices) {
        setFieldValue("services", filteredServices);
      }
    } else if (serviceId !== 1 && serviceId !== 111) {
      if (services?.length) {
        setFieldValue("services", [...services, serviceId]);
      } else {
        setFieldValue("services", [serviceId]);
      }
    }
    if (serviceId === 111 && !services?.filter((ser) => ser === 111)?.length) {
      setCourierModalOpen(true);
    }
    if (serviceId === 1 && !services?.find((ser) => ser === 1)) {
      handleOpenModal(ParcelModalType.DIVIDE);
    }
  };

  const handleOpenModal = (type: ParcelModalType) => {
    setModalType(type);
    openModal();
  };

  const handleCloseModal = () => {
    setModalType(null);
    closeModal();
  };

  const handleClickSkipButton = async () => {
    if (!isEditable) return;
    if (services !== createParcelData?.shipmentServiceIds && !parcelId) {
      const filteredServices = createParcelData?.shipmentServiceIds?.filter(
        (service) =>
          editableServices?.data?.data.some((e) => e.serviceId === service),
      );
      setFieldValue("services", filteredServices);
    }
    try {
      const validationResponse = await validateForm();
      if (validationResponse) {
        if (Object.keys(validationResponse).length === 0) {
          // closeModal();
          submitForm();
        } else {
          for (const [key, value] of Object.entries(validationResponse)) {
            console.error(`Key: ${key}, Value: ${value}`);
            if (key === "serviceDivideInformation") {
              toast.error(
                `Please fill in information about devided parcel or turn this service off`,
                {
                  position: "top-center",
                },
              );
            } else if (typeof value === "string") {
              toast.error(`${key}: ${value}`, {
                position: "top-center",
              });
            } else {
              toast.error(`${key}: incorrect or empty`, {
                position: "top-center",
              });
            }
          }
          console.error(validationResponse);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickMainButton = async () => {
    try {
      const validationResponse = await validateForm();
      if (validationResponse) {
        if (Object.keys(validationResponse).length === 0) {
          // apply georgian services manual validations
          if (products?.length === 0 && theme !== Theme.GEORGIA) return;

          const productsTotalPrice = getTotalPrice(products);

          // start 28th bug about invoice:
          if (
            productsTotalPrice * shipmenPrices?.currentRateNationalBank >
              shipmenPrices?.customPriceLimitNational &&
            !values.invoiceFile?.fileContentInBase64
          ) {
            setErrors({
              invoiceFile: {
                fileExtension: "invoice_file_required",
              },
            });
            toast.error(t("invoice_file_required"), {
              position: "top-center",
            });
            validationStepStatusRef.current.invoiceFile = false;
          } else {
            validationStepStatusRef.current.invoiceFile = true;
          }
          // end 28th bug about invoice:

          // start 29th bug about additional insurance:
          if (
            services.includes(113) &&
            !validationStepStatusRef.current.additionalInsurance
          ) {
            if (
              productsTotalPrice >
              shipmenPrices?.shipmentDeclaredCostForMinInsuranceInUsd
            ) {
              handleOpenModal(ParcelModalType.GUARANTEE);
              validationStepStatusRef.current.additionalInsurance = false;
            } else {
              validationStepStatusRef.current.additionalInsurance = true;
            }
          } else {
            validationStepStatusRef.current.additionalInsurance = true;
          }
          // end 29th bug about additional insurance:

          // start 30th bug about customs declaration:
          if (services.includes(208)) {
            if (
              productsTotalPrice * shipmenPrices?.currentRateNationalBank <
              shipmenPrices?.customPriceLimitNational
            ) {
              toast.error(t("validations_warning_custom_declaration"), {
                position: "top-center",
              });
              validationStepStatusRef.current.customDeclaration = false;
            } else {
              validationStepStatusRef.current.customDeclaration = true;
            }
          } else {
            validationStepStatusRef.current.customDeclaration = true;
          }
          // end 30th bug about customs declaration

          // start 31st bug about customs limit control
          // if (services.includes(209)) {
          //   if (
          //     productsTotalPrice * shipmenPrices?.currentRateNationalBank >
          //     shipmenPrices?.customPriceLimitNational
          //   ) {
          //     toast.error(t("validations_warning_custom_price_limit"), {
          //       position: "top-center",
          //     });
          //     validationStepStatusRef.current.customPriceLimit = false;
          //   } else {
          //     validationStepStatusRef.current.customPriceLimit = true;
          //   }
          // } else {
          //   validationStepStatusRef.current.customPriceLimit = true;
          // }
          // end 31st bug about customs limit control

          if (
            validationStepStatusRef.current.customDeclaration &&
            validationStepStatusRef.current.customPriceLimit &&
            validationStepStatusRef.current.invoiceFile &&
            validationStepStatusRef.current.additionalInsurance
          ) {
            submitForm();
            return;
          }
        } else {
          for (const [key, value] of Object.entries(validationResponse)) {
            console.error(`Key: ${key}, Value: ${value}`);
            if (
              key === "serviceDivideInformation" &&
              services?.find((ser) => ser === 1)
            ) {
              toast.error(
                t("validations_warning_fill_in_devider_information"),
                {
                  position: "top-center",
                },
              );
              handleOpenModal(ParcelModalType.DIVIDE);
            } else if (typeof value === "string") {
              toast.error(`${key}: ${value}`, {
                position: "top-center",
              });
            } else {
              toast.error(`${key}: incorrect or empty`, {
                position: "top-center",
              });
            }
          }
          console.error(validationResponse);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // needs additional logic for extra insurance
  const handleToggleSubmission = (term: boolean) => {
    validationStepStatusRef.current.additionalInsurance = true;
    setFieldValue("isInsuranceAddPayment", term);
    handleCloseModal();
    handleClickMainButton();
  };

  useEffect(() => {
    if (deliveryRequestStatus) {
      if (services?.length) {
        setFieldValue("services", [...services, 111]);
      } else {
        setFieldValue("services", [111]);
      }
    }
  }, [deliveryRequestStatus]);

  const DeliveryIcon = additionalServicesIcons.find(
    (icon) => icon.serviceId === 111,
  )?.icon;

  useEffect(() => {
    if (parcelStatus && !isEditable) {
      const parcelServices = currentParcelData?.data?.data?.shipmentServiceIds;
      setFieldValue("services", parcelServices);
    }
  }, [parcelStatus, currentParcelData]);

  const deliveryIsAllowed = useMemo(() => {
    return deliveryAllowedStatuses.includes(parcelStatus);
  }, [parcelStatus]);

  const isServiceDisabled = (serviceId: number) => {
    if (!parcelId) return false;

    if (theme === Theme.GEORGIA) {
      if (
        nonEditableStatusesForDeliveryTypeAndServices.includes(parcelStatus)
      ) {
        return true;
      }
      // const allowedServicesForStatusses = [
      //   ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE,
      //   ParcelStatus.COMMON_STATUS_RECEIVED_AT_THE_INSTOCK,
      // ];

      // if (
      //   allowedServicesForStatusses.includes(parcelStatus) &&
      //   serviceId === 113
      // ) {
      //   return false;
      // }
    }

    if (serviceId === 113) {
      const hasInsuranceService =
        currentParcelData?.data?.data?.shipmentServiceIds?.includes(113);
      return hasInsuranceService ? !insuranceIsCancelable : !isEditable;
    }

    // if (theme === Theme.UZ && serviceId === 211) {
    //   return true;
    // }

    return !isEditable;
  };

  return (
    <CommonFormLayout
      title={t("choose_addon_services")}
      skipButtonOnClick={handleClickSkipButton}
      mainButton={t("complete")}
      // mainBtnDisabled={!isEditable}
      secondaryButtonText={isEditable && t("back")}
      secondaryOnClick={() => setActiveStep(activeStep - 1)}
      mainButtonOnClick={handleClickMainButton}
      // noSubmit={theme === Theme.GEORGIA}
      noSubmit
    >
      <div className="w-[420px]">
        <div className="flex flex-col gap-[1rem]">
          {deliveryIsAllowed && (
            <div>
              <Toggle
                icon={<DeliveryIcon />}
                title={"Delivery service"}
                subTitle={""}
                isOn={deliveryRequestStatus}
                handleToggle={() => setCourierModalOpen(true)}
              />
            </div>
          )}
          {editableServices?.data?.data?.map((service, i) => {
            const Icon = additionalServicesIcons.find(
              (icon) => icon.serviceId === service.serviceId,
            )?.icon;

            const openDivideModal = () => {
              handleOpenModal(ParcelModalType.DIVIDE);
            };

            const disabled = isServiceDisabled(service.serviceId);

            const servicePrice =
              theme === Theme.UZ
                ? service.servicePriceNational
                : service.servicePrice;

            return (
              <div key={i}>
                <Toggle
                  icon={Icon ? <Icon /> : null}
                  title={service.serviceName}
                  subTitle={
                    theme === Theme.GEORGIA && service.serviceId === 113
                      ? formatNumber(service?.baseCostForServiceInsuranceInUsd)
                      : formatNumber(servicePrice)
                  }
                  isOn={
                    services?.filter(
                      (ser) => ser === service.serviceId,
                    )?.[0] === service.serviceId
                  }
                  handleToggle={() =>
                    service.serviceId === 113 &&
                    insuranceIsCancelable &&
                    services?.find((service: number) => service === 113)
                      ? cancelInsuranceRequest({ shipmentId: parcelId })?.then(
                          () => handleToggle(service.serviceId),
                        )
                      : handleToggle(service.serviceId)
                  }
                  disabled={disabled}
                  serviceId={service.serviceId}
                  handleEdit={
                    isEditable &&
                    service.serviceId === 1 &&
                    services?.find((service) => service === 1) &&
                    openDivideModal
                  }
                  startSymbol={
                    service.serviceId !== 113 && theme === Theme.UZ
                      ? "UZS "
                      : "$"
                  }
                  endSymbol={
                    theme === Theme.GEORGIA && service?.serviceId === 113
                      ? ""
                      : (theme === Theme.UK || theme === Theme.UZ) &&
                          service?.serviceId === 113
                        ? "%"
                        : ""
                  }
                />
                {/* @ts-ignore */}
                {service.serviceId === 205 && services?.includes(205) && (
                  <WarningBox showIcon text="parcel_calculation_label" />
                )}
                {/* @ts-ignore */}
                {service.serviceId === 888 && services?.includes(888) && (
                  <UploadButton
                    rightIcon={<UploadIcon />}
                    className="mt-[14px]"
                  >
                    Upload returning document
                  </UploadButton>
                )}
                {/* @ts-ignore */}
                {service.serviceId === 201 && services?.includes(201) && (
                  <Textarea
                    label="add_comment"
                    id="servicePhotoOfContentsComment"
                    resize="resize-none"
                    className="h-[9.375rem] !rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                    containerClassName="mt-[14px]"
                    labelClassName="!left-[2rem]"
                    value={servicePhotoOfContentsComment}
                    onChange={(e) => {
                      setFieldValue(
                        "servicePhotoOfContentsComment",
                        (e.target as HTMLTextAreaElement).value,
                      );
                    }}
                  />
                )}
                {/* @ts-ignore */}
                {service.serviceId === 202 && services?.includes(202) && (
                  <Textarea
                    label="add_comment"
                    id="serviceFunctionalityCheckComment"
                    resize="resize-none"
                    className="h-[9.375rem] !rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                    containerClassName="mt-[14px]"
                    labelClassName="!left-[2rem]"
                    value={serviceFunctionalityCheckComment}
                    onChange={(e) => {
                      setFieldValue(
                        "serviceFunctionalityCheckComment",
                        (e.target as HTMLTextAreaElement).value,
                      );
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        closeModal={handleCloseModal}
        showCloseIcon
        title=""
        titleClassNames="uppercase"
        className="w-full max-w-[465px]"
      >
        {modalType === ParcelModalType.DIVIDE && theme === Theme.UZ && (
          <ParcelDividerModalUz
            handleAddInfo={async () => {
              try {
                setFieldTouched("serviceDivideInformation", true, true);

                await validateField("serviceDivideInformation");

                if (!errors.serviceDivideInformation) {
                  handleCloseModal();
                  setFieldValue("services", [...services, 1]);
                }
              } catch (error) {
                console.error(error);
              }
            }}
          />
        )}

        {modalType === ParcelModalType.DIVIDE && theme === Theme.UK && (
          <ParcelDividerModalUa
            handleAddInfo={async () => {
              try {
                console.log("values oooo", values);

                setFieldTouched("serviceDivideInformation", true, true);

                await validateField("serviceDivideInformation");

                if (!errors.serviceDivideInformation) {
                  handleCloseModal();
                  setFieldValue("services", [...services, 1]);
                }
              } catch (error) {
                console.error(error);
              }
            }}
          />
        )}

        {modalType === ParcelModalType.GUARANTEE && (
          <ParcelGuaranteeGeModal
            onSubmit={handleToggleSubmission}
            productPrice={Number(values.productPriceUsd)}
          />
        )}
      </Modal>
    </CommonFormLayout>
  );
};

export default ParcelServices;
